/**
 * LANE V2 — the session view's pure state rules.
 *
 * THE ONE RULE: FRAMES DECIDE WHAT TO REFETCH, SNAPSHOTS ARE THE TRUTH. The session stream carries
 * ids and counts only (`ask_raised` has no prompt, `plan_proposed` no items, `item_built` no names),
 * so nothing here assembles plan / package / ask state from a frame payload. A frame advances the
 * cursor, flips the two facts a frame genuinely owns (a turn opened / ended), and returns a
 * `RefetchPlan` naming which snapshot GETs are now stale:
 *
 *   ask_*                                   → R2 (asks)
 *   task frames with a task_id              → R3 for that task + R1 (the task list)
 *   task frames without one                 → R1
 *   turn_opened / turn_ended                → R5 (+ R1 on turn_ended: a turn may have opened a task)
 *   anything else                           → nothing (the frame type set is OPEN)
 *
 * Idempotent by `seq`: a frame at or below the cursor changes nothing and asks for nothing, so a
 * replay after a reconnect, a duplicate, or an out-of-order straggler is harmless by construction.
 */
import type {
  AskView,
  ConnectorWriteOutcome,
  LaneV2Session,
  SessionFrameWire,
  TaskSnapshot,
  TaskSummary,
  TurnAttachmentView,
  TurnView,
  UsageTotals,
} from "./types";
import { TERMINAL_TASK_STATES } from "./types";
import type { StreamStatus } from "./stream";
import { activityRowForFrame, activityRowForLive, appendActivity, applyDraftFrame, type ActivityRow, type LiveDraft, type LiveFrameWire } from "./activity";
import { readableUserText, unconfirmFollowUps, type FollowUp } from "./followUps";

/** A message the user sent, shown before (and, if R5 lacks `user_text`, instead of) the server's copy. */
export interface LocalTurn {
  localId: string;
  text: string;
  at: string;
  /** Bound from `TurnOut` or the first `turn_opened` after the send. */
  turnId: string | null;
  status: "sending" | "sent" | "failed";
  error?: string | null;
  /** `TurnOut.markdown` — a bonus copy of the answer, used only while R5 has none. */
  markdown?: string | null;
  /** What rode the send (name and kind only — never the bytes), shown on the question until R5 lists the stored ones. */
  attachments?: LocalAttachment[];
}

/** An attachment as the reader sent it, before the server has stored it and given it an id. */
export interface LocalAttachment {
  name: string;
  kind: string;
  /**
   * The image itself, as the composer previewed it (a data URL) — THIS BROWSER's copy, in memory only (never persisted,
   * never sent). MSA stores an image as the text it read from it (`lane_v2/intake.py` `_store_one`), so this is the only
   * place the picture still exists once it is sent; a reload loses it and the description is what remains.
   */
  previewUrl?: string;
}

/** A turn's attachment as the transcript shows it: R5's stored one (opens by id), or the echo's (id null, not yet stored). */
export type ConversationAttachment = Omit<TurnAttachmentView, "attachment_id"> & {
  attachment_id: string | null;
  /** This browser's copy of the image, when it still has one (`LocalAttachment.previewUrl`). */
  previewUrl?: string;
};

/**
 * A branch save this browser is tracking, per task (a client view of the server's job, not a second truth: R3
 * `package.branch_save_running` is what says a save is running). `running` carries the mode and the polled chunk count;
 * `failed` keeps the error a resumed (unattended) poll ended with, for the save panel to classify.
 */
export type TrackedSave =
  | { phase: "running"; saveId: string | null; mode: "async" | "sync"; chunks: number }
  | { phase: "failed"; error: unknown; headAt?: string | null };

export interface LaneV2State {
  sessionId: string | null;
  /** Branch saves being followed, by task id. Cleared on attach/reset. */
  saves: Record<string, TrackedSave>;
  /** A client-owned id nothing has been sent on yet — not pinned server-side, never streamed. */
  draft: boolean;
  lastSeq: number;
  tasks: TaskSummary[];
  snapshots: Record<string, TaskSnapshot>;
  asks: AskView[];
  turns: TurnView[];
  activeTurnId: string | null;
  localTurns: LocalTurn[];
  streamStatus: StreamStatus;
  /** A user-facing error line (fixed copy), or null. */
  error: string | null;
  /** A transient notice ("busy, retrying"), or null. */
  notice: string | null;
  /** From R0 — whether apply can be approved on this environment. */
  deployMode: string | null;
  /** From R0 `turn_answers_ask` — gates `askRouting.ts`'s `respond_then_turn` route. */
  turnAnswersAsk: boolean;
  /** R1 `usage_total` — the conversation's own running spend. `UsageMeter` reads this for lane v2. */
  usageTotal: UsageTotals | null;
  /** From R0 `offline` — the operator kill switch. Disables Send/approve; declines and cancel stay on. */
  offline: boolean;
  /** From R0 `turn_attachments` — gates mounting the composer's attachment affordances at all. */
  turnAttachments: boolean;
  /** From R0 `turn_ide_state` — gates sending `ide_state` with every turn. */
  turnIdeState: boolean;
  /** From R0 `branch_save` — gates `PackageView`'s "Save to branch" action. */
  branchSave: boolean;
  /** From R0 `live_frames` — the stream may carry transient `turn_activity` frames. */
  liveFrames: boolean;
  /**
   * What the session has been doing, in words, newest last (`activity.ts`). Fed by journal frames
   * and live frames; read by the turn story. Not a second truth: every row is a frame restated.
   */
  activity: ActivityRow[];
  /** When the newest send started, for the elapsed clock; null once nothing is in flight. */
  workingSince: string | null;
  /** The reply as the session seat writes it (`turn_text_delta`); cleared when the turn ends or R5 has the reply. */
  liveDraft: LiveDraft | null;
  /**
   * What each approved write to a connected tool did, by ask id (`connector_write_done` frames and the respond payload's
   * `applied_to`). Read by the settled ask line. In-memory only: a reload shows the generic line until the chat is asked.
   */
  connectorWrites: Record<string, ConnectorWriteOutcome>;
  /** A refetch after a frame failed: what is on screen may be out of date until the next successful one (or a Refresh). */
  stale: boolean;
  /**
   * What the reader sent WHILE a turn was running (`followUps.ts`): each went to that turn's inbox and is settled by the
   * turn's own result. In-memory only: R5 does not carry them, so a reload shows the turn without them.
   */
  followUps: FollowUp[];
  /** Messages a turn did not act on, waiting to be sent as the next turn(s), oldest first. One is sent each time the session is idle. */
  queuedTurns: string[];
}

export const INITIAL_LANE_V2_STATE: LaneV2State = {
  sessionId: null,
  saves: {},
  draft: false,
  lastSeq: 0,
  tasks: [],
  snapshots: {},
  asks: [],
  turns: [],
  activeTurnId: null,
  localTurns: [],
  streamStatus: "idle",
  error: null,
  notice: null,
  deployMode: null,
  turnAnswersAsk: false,
  usageTotal: null,
  offline: false,
  turnAttachments: false,
  turnIdeState: false,
  branchSave: false,
  liveFrames: false,
  activity: [],
  workingSince: null,
  liveDraft: null,
  followUps: [],
  queuedTurns: [],
  connectorWrites: {},
  stale: false,
};

export interface RefetchPlan {
  session: boolean;
  asks: boolean;
  turns: boolean;
  tasks: string[];
}

export const NO_REFETCH: RefetchPlan = Object.freeze({ session: false, asks: false, turns: false, tasks: [] }) as RefetchPlan;

export function isEmptyPlan(plan: RefetchPlan): boolean {
  return !plan.session && !plan.asks && !plan.turns && plan.tasks.length === 0;
}

/** Union of two plans (the debounce coalesces with this). */
export function mergePlans(a: RefetchPlan, b: RefetchPlan): RefetchPlan {
  return {
    session: a.session || b.session,
    asks: a.asks || b.asks,
    turns: a.turns || b.turns,
    tasks: Array.from(new Set([...a.tasks, ...b.tasks])),
  };
}

const ASK_FRAMES: ReadonlySet<string> = new Set(["ask_raised", "ask_answered", "ask_withdrawn", "ask_expired"]);

/** Frames that change something R3 (and the R1 task list) serves. */
export const TASK_FRAMES: ReadonlySet<string> = new Set([
  "task_transitioned",
  "plan_proposed",
  "plan_revised",
  "build_materialized",
  "item_built",
  "item_failed",
  "level_done",
  "build_parked",
  "build_terminal",
  "deviation_proposed",
  "package_assembled",
  "check_finished",
  // A chat "save it" / a recorded verdict changes what R3's package and validation serve (UX map C3).
  "branch_saved",
  "verdict_recorded",
  "apply_finished",
  "reverted",
  "grant_minted",
  "grant_revoked",
  // The save-in-flight seam (R3 `package.branch_save_running`): a reserved chunk, a background-save state change, and the
  // other facts R3 serves that a frame announces (check settle, drift, a connector write).
  "branch_save_requested",
  "branch_save_job",
  "level_check_settled",
  "drift_detected",
  "connector_write_drafted",
  "connector_write_requested",
  "connector_write_done",
  "connector_write_failed",
]);

/** Turn statuses that mean "a turn is running" (MSA `dialogue.machines.TurnStatus`: queued → running → terminal). */
export const ACTIVE_TURN_STATUSES: ReadonlySet<string> = new Set(["queued", "running"]);

function str(v: unknown): string | null {
  return typeof v === "string" && v ? v : null;
}

/** Which snapshot GETs a frame makes stale. Pure; ignores the cursor. */
export function refetchFor(frame: Pick<SessionFrameWire, "type" | "task_id">): RefetchPlan {
  if (ASK_FRAMES.has(frame.type)) return { ...NO_REFETCH, asks: true };
  if (TASK_FRAMES.has(frame.type)) {
    return { ...NO_REFETCH, session: true, tasks: frame.task_id ? [frame.task_id] : [] };
  }
  if (frame.type === "turn_opened") return { ...NO_REFETCH, turns: true };
  if (frame.type === "turn_ended") return { ...NO_REFETCH, turns: true, session: true };
  return NO_REFETCH;
}

/** Bind the oldest unbound, sent-or-sending local echo to a turn id (first `turn_opened` / TurnOut wins). */
export function bindLocalTurn(localTurns: LocalTurn[], turnId: string, localId?: string): LocalTurn[] {
  if (localTurns.some((t) => t.turnId === turnId)) return localTurns;
  const at = localId
    ? localTurns.findIndex((t) => t.localId === localId && !t.turnId)
    : localTurns.findIndex((t) => !t.turnId && t.status !== "failed");
  if (at === -1) return localTurns;
  return localTurns.map((t, i) => (i === at ? { ...t, turnId } : t));
}

/**
 * A `ConnectorWriteOutcome` read off a respond payload's `applied_to` or a `connector_write_done` frame payload, or null
 * when the value is not one (another `applied_to` type, a malformed frame). Only `http(s)` urls are kept.
 */
export function connectorWriteOutcomeOf(value: unknown): ConnectorWriteOutcome | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (v.type !== undefined && v.type !== "connector_write") return null;
  const askId = str(v.ask_id);
  if (!askId || typeof v.ok !== "boolean") return null;
  const url = str(v.url);
  return {
    ask_id: askId,
    provider: str(v.provider) ?? "",
    action: str(v.action) ?? "",
    ok: v.ok,
    url: url && /^https?:\/\//i.test(url) ? url : null,
    title: str(v.title),
    error: str(v.error),
  };
}

/**
 * Apply one frame. Returns the SAME state object (and `NO_REFETCH`) for a frame at or below the
 * cursor, so a caller can cheaply tell nothing happened.
 */
export function reduceFrame(state: LaneV2State, frame: SessionFrameWire): { state: LaneV2State; refetch: RefetchPlan } {
  if (!(frame.seq > state.lastSeq)) return { state, refetch: NO_REFETCH };
  let next: LaneV2State = { ...state, lastSeq: frame.seq };
  const row = activityRowForFrame(frame);
  if (row) next = { ...next, activity: appendActivity(next.activity, row) };
  if (frame.type === "connector_write_done") {
    const outcome = connectorWriteOutcomeOf(frame.payload);
    if (outcome) next = { ...next, connectorWrites: { ...next.connectorWrites, [outcome.ask_id]: outcome } };
  }
  if (frame.type === "turn_opened") {
    const turnId = str(frame.payload?.turn_id);
    if (turnId) next = { ...next, activeTurnId: turnId, localTurns: bindLocalTurn(next.localTurns, turnId) };
  } else if (frame.type === "turn_ended") {
    const turnId = str(frame.payload?.turn_id);
    // Clear when it is the turn we think is active, or when we cannot tell which one ended.
    if (!turnId || !next.activeTurnId || next.activeTurnId === turnId) next = { ...next, activeTurnId: null, workingSince: null };
    // The draft stays until R5 holds the turn's reply (`applyTurns`), so the prose never blanks in the hand-off.
    // The turn is over; until its own result says what it did with them, its waiting messages are unconfirmed.
    if (turnId && next.followUps.length) next = { ...next, followUps: unconfirmFollowUps(next.followUps, turnId) };
  }
  return { state: next, refetch: refetchFor(frame) };
}

/** A transient live frame: a row for the story, nothing else (no cursor, no refetch). */
export function reduceLiveFrame(state: LaneV2State, frame: LiveFrameWire): LaneV2State {
  if (frame.type === "turn_text_delta") {
    // Only the active turn has a draft: a late chunk of a finished (or another) turn is dropped.
    if (!frame.turn_id || frame.turn_id !== state.activeTurnId) return state;
    const liveDraft = applyDraftFrame(state.liveDraft, frame);
    return liveDraft === state.liveDraft ? state : { ...state, liveDraft };
  }
  const row = activityRowForLive(frame);
  return row ? { ...state, activity: appendActivity(state.activity, row) } : state;
}

// ── snapshot setters (the truth) ─────────────────────────────────────────────────────────────────

/** R1. Does NOT move the cursor (see `applyAttach` for the one place that may). */
export function applySession(state: LaneV2State, session: LaneV2Session): LaneV2State {
  return {
    ...state,
    tasks: Array.isArray(session.tasks) ? session.tasks : [],
    // A server that omits usage_total (defensive — the spec says it is always a dict) keeps whatever
    // this client last knew rather than blanking a real figure to null.
    usageTotal: session.usage_total ?? state.usageTotal,
  };
}

/**
 * The attach snapshot: R1 read FIRST, then R2/R5/R3 — so everything read afterwards reflects at
 * least `R1.last_seq`, and the stream may start from there. Only here does a snapshot move the cursor
 * (a mid-stream R1 must not: frames between the cursor and its `last_seq` would then be dropped
 * without their refetches).
 */
export function applyAttach(
  state: LaneV2State,
  data: { session: LaneV2Session; asks: AskView[]; turns: TurnView[]; snapshots: Record<string, TaskSnapshot> },
): LaneV2State {
  const withSession = applySession(state, data.session);
  const withTurns = applyTurns(withSession, data.turns);
  return {
    ...withTurns,
    asks: data.asks,
    stale: false,
    snapshots: { ...state.snapshots, ...data.snapshots },
    lastSeq: Math.max(state.lastSeq, typeof data.session.last_seq === "number" ? data.session.last_seq : 0),
  };
}

export function applyAsks(state: LaneV2State, asks: AskView[]): LaneV2State {
  return { ...state, asks };
}

/** R5. Also re-derives the active turn from the server's statuses, and retires matched local echoes. */
export function applyTurns(state: LaneV2State, turns: TurnView[]): LaneV2State {
  const running = [...turns].reverse().find((t) => ACTIVE_TURN_STATUSES.has(t.status));
  const known = new Map(turns.map((t) => [t.turn_id, t]));
  const localTurns = state.localTurns.filter((local) => {
    if (!local.turnId) return true;
    const server = known.get(local.turnId);
    // Keep the echo while the server lacks what the echo still supplies (the question, or the
    // TurnOut answer) — the timeline reads both through it.
    if (!server) return true;
    /*
     * Not while its POST is still out. MSA answers `POST /turns` only when the turn ENDS, so `turn_opened` bound this
     * echo and R5 listed the turn long before the request came back — and retiring the echo then erased the only
     * record that request's outcome is written to: "sent" (what lets the composer drop what rode the turn) and
     * "failed" both landed on nothing. The image stayed in the composer after the answer (QA, 2026-10-15). The
     * timeline merges a bound echo into its server turn, so keeping it shows nothing twice.
     */
    if (local.status === "sending") return true;
    // Nor while it holds an image the server cannot give back (`LocalAttachment.previewUrl`): it is merged into its
    // server turn, so keeping it shows nothing twice — it only lends that turn the picture.
    if (local.attachments?.some((a) => a.previewUrl)) return true;
    return !server.user_text || (!!local.markdown && !server.markdown);
  });
  const activeTurnId = running ? running.turn_id : null;
  const sending = localTurns.some((t) => t.status === "sending");
  // R5 is the truth: a draft ends with its turn, or once the server holds that turn's reply.
  const draftTurn = state.liveDraft ? known.get(state.liveDraft.turnId) : undefined;
  // Kept while that turn is still running or not yet in R5 (it may have just ended: `turn_ended` keeps the draft).
  const draftOpen = !!state.liveDraft && (draftTurn ? ACTIVE_TURN_STATUSES.has(draftTurn.status) && !draftTurn.markdown : activeTurnId === state.liveDraft.turnId);
  const liveDraft = draftOpen ? state.liveDraft : null;
  return { ...state, turns, localTurns, activeTurnId, liveDraft, workingSince: activeTurnId || sending ? state.workingSince : null };
}

export function applySnapshot(state: LaneV2State, snapshot: TaskSnapshot): LaneV2State {
  return { ...state, snapshots: { ...state.snapshots, [snapshot.task_id]: snapshot } };
}

// ── derived views ────────────────────────────────────────────────────────────────────────────────

/** Tasks for the panel list: open tasks first (newest first), then closed ones (newest first). */
export function orderedTasks(tasks: readonly TaskSummary[]): TaskSummary[] {
  const byNewest = [...tasks].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const open = byNewest.filter((t) => !TERMINAL_TASK_STATES.has(t.state));
  const closed = byNewest.filter((t) => TERMINAL_TASK_STATES.has(t.state));
  return [...open, ...closed];
}

export function pendingAsks(asks: readonly AskView[]): AskView[] {
  return asks.filter((a) => a.status === "pending");
}

/** One row of the transcript. */
export type TimelineEntry =
  | {
      kind: "turn";
      key: string;
      at: string;
      turnId: string;
      userText: string | null;
      markdown: string | null;
      status: string;
      /** R5 `outcome` (MSA `DispatchOutcome`: answered, plan_proposed, failed, …), null while open. */
      outcome: string | null;
      /** R5 `task_id` — the task this turn opened or acted on, or null. */
      taskId: string | null;
      errorCode: string | null;
      /** R5's per-turn `usage` — null when the turn spent nothing (leg `v2-usage-wire`). */
      usage: UsageTotals | null;
      /** R5's per-turn `attachments` (`[]` when none), else what the echo sent while R5 has not listed them yet. */
      attachments: ConversationAttachment[];
      /** R5 `trace.tool_calls` — how many tool calls the turn made (leg `gui-what-i-read`), null when the server did not say. */
      toolCalls?: number | null;
    }
  | { kind: "local"; key: string; at: string; local: LocalTurn }
  | { kind: "ask"; key: string; at: string; ask: AskView };

/**
 * The transcript: server turns (with the local echo's text standing in for a missing `user_text`),
 * asks, and not-yet-acknowledged local echoes, in time order. Unbound/unmatched echoes go last —
 * they are the newest thing the user did.
 */
/** The echo's attachments as transcript rows — no id yet, so they show but do not open. */
export function echoAttachments(local: LocalTurn | undefined): ConversationAttachment[] {
  return (local?.attachments ?? []).map((a) => ({
    attachment_id: null,
    name: a.name,
    kind: a.kind,
    ...(a.previewUrl ? { previewUrl: a.previewUrl } : {}),
  }));
}

/**
 * R5's stored attachments, each lent this browser's picture when the echo sent one by that name and kind. Matched once
 * each, in order, so two files with one name keep their own pictures. MSA stores the name as sent (clipped to 256).
 */
export function withEchoPreviews(stored: readonly TurnAttachmentView[], local: LocalTurn | undefined): ConversationAttachment[] {
  const pool = (local?.attachments ?? []).filter((a) => a.previewUrl);
  if (!pool.length) return [...stored];
  return stored.map((s) => {
    const at = pool.findIndex((p) => p.kind === s.kind && p.name.slice(0, 256) === s.name);
    if (at === -1) return s;
    const [match] = pool.splice(at, 1);
    return { ...s, previewUrl: match.previewUrl };
  });
}

export function timeline(state: Pick<LaneV2State, "turns" | "asks" | "localTurns">): TimelineEntry[] {
  const echoByTurn = new Map(state.localTurns.filter((l) => l.turnId).map((l) => [l.turnId as string, l]));
  const serverTurnIds = new Set(state.turns.map((t) => t.turn_id));
  const dated: TimelineEntry[] = [
    ...state.turns.map<TimelineEntry>((t) => ({
      kind: "turn",
      key: `turn:${t.turn_id}`,
      at: t.created_at,
      turnId: t.turn_id,
      userText: readableUserText(t.user_text ?? null) ?? echoByTurn.get(t.turn_id)?.text ?? null,
      markdown: t.markdown ?? echoByTurn.get(t.turn_id)?.markdown ?? null,
      status: t.status,
      outcome: t.outcome ?? null,
      taskId: t.task_id ?? null,
      errorCode: t.error_code ?? null,
      usage: t.usage ?? null,
      attachments:
        Array.isArray(t.attachments) && t.attachments.length > 0
          ? withEchoPreviews(t.attachments, echoByTurn.get(t.turn_id))
          : echoAttachments(echoByTurn.get(t.turn_id)),
      toolCalls: typeof t.trace?.tool_calls === "number" ? t.trace.tool_calls : null,
    })),
    ...state.asks.map<TimelineEntry>((a) => ({
      kind: "ask",
      key: `ask:${a.interaction.interaction_id}`,
      at: a.created_at,
      ask: a,
    })),
  ].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  const trailing = state.localTurns
    .filter((l) => !l.turnId || !serverTurnIds.has(l.turnId))
    .map<TimelineEntry>((l) => ({ kind: "local", key: `local:${l.localId}`, at: l.at, local: l }));
  return [...dated, ...trailing];
}
